import { lensApi } from "@/services/lensApi";
import { getSearchParamForRequest, getStructureFilterParamsForRequst, StructureFilterParams } from "@/utils/filters";

export type StructureResponse = Dryp.ApiList<Dryp.Structure>;
export type StructureTypesResponse = Dryp.ApiList<Dryp.StructureType>;
export type StructureGroupsResponse = Dryp.ApiList<Dryp.StructureGroup>;

// Endpoint-specific extension of the shared filter set. `parameterIds` only applies to
// the /structures list endpoint so it lives here rather than in the general filter utils.
export interface StructureListFilters extends StructureFilterParams {
	parameterIds?: Dryp.ParameterId[];
}

interface UpdateStructureRequest {
	structureId: Dryp.StructureId;
	name?: string;
	description?: string;
	coordinate?: {
		latitude: number;
		longitude: number;
	};
	structureTypeId?: Dryp.StructureTypeId;
	externalIdString?: string;
	structureGroupIds?: Dryp.StructureGroupId[];
	networkType?: Dryp.NetworkType;
}

let structuresData: Dryp.Structure[] = [];

export const structure = {
	getAllStructures: async (
		pagination: Dryp.PaginationQuery,
		structureFilters?: StructureListFilters,
		sort?: Dryp.SortQuery,
	): Promise<StructureResponse> => {
		const { parameterIds, ...sharedFilters } = structureFilters || {};
		const sharedFilterParams = getStructureFilterParamsForRequst(sharedFilters);
		const parameterFilterParam =
			parameterIds && parameterIds.length > 0 ? { "filter[parameterIds]": parameterIds } : {};

		const response = await lensApi.get<StructureResponse>("/structures", {
			params: {
				...pagination,
				...sort,
				...sharedFilterParams,
				...parameterFilterParam,
			},
		});

		if (response.items.length > 0) {
			structuresData = response.items;
		}

		return response;
	},
	getStructureParameters: async (structureId: Dryp.StructureId): Promise<Dryp.StructureParameter[]> => {
		return lensApi.get<Dryp.StructureParameter[]>(`/structures/${structureId}/parameters`);
	},
	editStructure: async (requestProperties: UpdateStructureRequest): Promise<UpdateStructureRequest> => {
		const { structureId, ...restRequestProperties } = requestProperties;
		return lensApi.put<UpdateStructureRequest>(`/structures/${structureId}`, restRequestProperties);
	},
	attachIAndISensors: async (structureId: Dryp.StructureId): Promise<void> => {
		return lensApi.post<void>(`/structures/${structureId}/i-and-i-sensors`, {});
	},
	removeIAndISensors: async (structureId: Dryp.StructureId): Promise<void> => {
		return lensApi.delete<void>(`/structures/${structureId}/i-and-i-sensors`);
	},
	getAllStructureTypes: async (pagination: Dryp.PaginationQuery): Promise<StructureTypesResponse> => {
		const response = await lensApi.get<StructureTypesResponse>(`/structure-types`, { params: pagination });

		if (structuresData.length > 0) {
			const structureTypes = response?.items || [];
			const countedStructureTypes = structureTypes
				.map(structureType => {
					return {
						...structureType,
						count: structuresData.filter(({ type }) => type.id === structureType.id).length,
					};
				})
				.sort((a, b) => b.count - a.count);
			return { ...response, items: countedStructureTypes };
		}

		return response;
	},
	getAllStructureGroups: async (
		pagination: Dryp.PaginationQuery,
		sort?: Dryp.SortQuery,
		search?: string,
	): Promise<StructureGroupsResponse> => {
		const response = await lensApi.get<StructureGroupsResponse>(`/structure-groups`, {
			params: { ...pagination, ...sort, ...getSearchParamForRequest(search) },
		});
		if (structuresData.length > 0) {
			const structureGroups = response?.items || [];
			const countedstructureGroups = structureGroups
				.map(structureGroup => {
					return {
						...structureGroup,
						count: structuresData.filter(({ groups }) => groups.find(({ id }) => id === structureGroup.id))
							.length,
					};
				})
				.sort((a, b) => b.count - a.count);
			return {
				...response,
				items: countedstructureGroups,
			};
		}

		return response;
	},
};
